<template>
    <div class="modal fade" id="openHC" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="exampleModalLabel">
                        <i class="fas fa-folder-open fa-fw"></i> Abrir Historia Clinica No. {{ historia_clinica.id }}
                    </h5>
                    <div class="d-flex justify-content-end mx-2">
                        <span class="badge badge-secondary-soft badge-marketing" v-if="historia_clinica.tipo_historia"> {{
                            historia_clinica.tipo_historia.nombre }}</span>
                    </div>
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <div class="d-flex mb-3">
                        <div class="justify-content-between mx-2 font-weight-bolder" v-if="historia_clinica.user">
                            <i class="fa fa-user fa-fw"></i> {{ historia_clinica.user.name }}
                        </div>
                        <div class="justify-content-end mx-2 font-weight-bolder">
                            <i class="fa fa-calendar fa-fw"></i> {{ historia_clinica.fecha | formatDate }}
                        </div>
                    </div>

                    <!-- Mensaje descriptivo nota aclaratoria -->
                    <div class="alert alert-secondary-soft border-left-lg border-secondary py-2 px-3 mb-3">
                        <div class="d-flex align-items-center">
                            <i class="fas fa-file-signature text-secondary fa-lg mr-3"></i>
                            <div>
                                <span class="font-weight-bold d-block text-dark small">Nota Aclaratoria Requerida</span>
                                <small class="text-muted">
                                    Para poder realizar ediciones en la historia clínica es necesario ingresar una nota aclaratoria detallando el motivo y la justificación de la modificación.
                                </small>
                            </div>
                        </div>
                    </div>

                    <div class="form-group mb-0">
                        <label class="col-sm-12 col-form-label font-weight-bolder px-2">
                            <i class="fa fa-comment fa-fw"></i> Nota Aclaratoria / Motivo de Apertura
                        </label>
                        <div class="col-sm-12">
                            <textarea cols="30" rows="5" class="form-control" v-model="motivo"
                                placeholder="Describa detalladamente la nota aclaratoria justificando las modificaciones que se realizarán en la historia clínica..."
                                :class="{ 'is-invalid': $v.motivo.$error && $v.motivo.$dirty }"></textarea>
                            <div v-if="$v.motivo.$dirty && $v.motivo.$invalid" class="text-danger text-sm mt-1">
                                <span v-if="!$v.motivo.required">Debe ingresar la nota aclaratoria.</span>
                                <span v-else-if="!$v.motivo.minLengthValue">Debe ingresar un mínimo de 10 caracteres.</span>
                                <span v-else-if="!$v.motivo.noCaracteresRepetidos || !$v.motivo.diversidadCaracteres">No se permiten caracteres repetidos consecutivos ni patrones de relleno.</span>
                                <span v-else-if="!$v.motivo.minimoLetrasReales">Debe ingresar una justificación con palabras reales (no solo puntos, signos o números).</span>
                                <span v-else-if="!$v.motivo.minimoPalabras">Debe ingresar una explicación justificada con sentido clínico (al menos 2 palabras).</span>
                                <span v-else-if="!$v.motivo.noTerminosGenericos">Ingrese una justificación clínica válida (evite términos como prueba, test, asdf, etc.).</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-success btn-sm" @click="guardar();">Abrir Historia<i
                            class="fa fa-folder-open fa-fw"></i></button>
                    <button type="button" class="btn btn-light btn-sm" data-dismiss="modal">Cerrar<i
                            class="fa fa-times-circle fa-fw"></i></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import historiaClinicaService from "../../../../../services/historiaClinicaService";
import $ from 'jquery';
import Toast from '../../../../../components/common/utilities/toast';
import { required, minLength } from "vuelidate/lib/validators";

// 1. Evita más de 3 caracteres idénticos consecutivos (ej: ....., xxxx, aaaa)
const noCaracteresRepetidos = (val) => !val || !/(.)\1{3,}/i.test(val);

// 2. Exige al menos 8 letras reales (evita que solo sean puntos, guiones o números)
const minimoLetrasReales = (val) => !val || (val.match(/[a-zA-ZáéíóúÁÉÍÓÚñÑ]/g) || []).length >= 8;

// 3. Exige al menos 2 palabras válidas con letras
const minimoPalabras = (val) => {
    if (!val) return true;
    const palabras = val.trim().split(/\s+/).filter(w => w.length >= 2 && /[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(w));
    return palabras.length >= 2;
};

// 4. Exige diversidad de caracteres (al menos 4 caracteres únicos diferentes)
const diversidadCaracteres = (val) => {
    if (!val) return true;
    return new Set(val.toLowerCase().replace(/\s+/g, '')).size >= 4;
};

// 5. Evita términos comunes de relleno
const noTerminosGenericos = (val) => {
    if (!val) return true;
    const limpio = val.toLowerCase().trim().replace(/[.,;:]/g, '');
    const invalidos = ['asdf', 'qwerty', 'prueba', 'test', 'ninguna', 'ninguno', 'no aplica', 'na', 'nada', 'abrir hc', 'abrir historia'];
    return !invalidos.includes(limpio);
};

export default {
    data() {
        return {
            motivo: '',
            historia_clinica: {}
        }
    },
    validations() {
        return {
            motivo: { 
                required,
                minLengthValue: minLength(10),
                noCaracteresRepetidos,
                minimoLetrasReales,
                minimoPalabras,
                diversidadCaracteres,
                noTerminosGenericos
            }
        }
    },
    methods: {
        async guardar() {
            try {

                this.$v.$touch();

                if (this.$v.$invalid) return;

                let motivos = {
                    id_historia: this.historia_clinica.id,
                    motivo: this.motivo,
                }

                this.LoaderSpinnerShow();
                await historiaClinicaService.openHc(motivos);
                this.LoaderSpinnerHide();
                this.limpiarCampos();

                $(`#openHC`).modal('hide');

                Toast.fire({
                    icon: 'success',
                    title: 'Historia Ha sido abierta con exito'
                });

                this.$router.push({
                    name : this.historia_clinica.tipo_historia.path,
                    params : {
                        id : this.historia_clinica.id
                    }
                });

            } catch (error) {
                this.LoaderSpinnerHide();
                Toast.fire({
                    icon: 'error',
                    title: 'Ocurrio un error al procesar la solicitud'
                });
            }
        },
        limpiarCampos() {
            this.motivo = '';
        },
        openModal(historia) {
            this.$nextTick(async () => {
                $('#openHC').modal('show');
                this.historia_clinica = historia;
            });
        }

    },
}
</script>
